import { wallOpeningOpening3 } from "building-common/openings/openingQuery";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { planeNormal, planeProjectPolyLine3 } from "geom/plane";
import { type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3OnPlane } from "geom/polyLine3";
import { planExtentsFromElevations } from "./openingUtils";
import { wallOpeningNew } from "./wallOpeningNew";

export function wallCutOpeningNew(
  wallCut: WallCut,
  frontPlacement: OpeningSideHolePlacement,
  frontHole: OpeningSide,
  backPlacement: OpeningSideHolePlacement,
  backHole: OpeningSide,
): WallOpening {
  const frontPlane = frontPlacement.openingPlane;
  const backPlane = backPlacement.openingPlane;

  // the cut plane for the hole does not always match up with the
  // exterior wall planes, so we need to project it
  let frontOutline3: PolyLine3 | undefined;
  let frontOutline2OnFrontPlane: PolyLine2 | undefined;
  if (!!wallCut.opening?.front?.opening3) {
    frontOutline3 = wallOpeningOpening3(wallCut.opening, "FRONT");
    assertIsNotNullOrUndefined(frontOutline3);
    frontOutline2OnFrontPlane = planeProjectPolyLine3(frontPlane, frontOutline3);
  } else {
    frontOutline3 = polyLine3OnPlane(frontHole.opening2, frontPlacement.edgePlane);
    frontOutline2OnFrontPlane = planeProjectPolyLine3(frontPlane, frontOutline3);
    frontOutline3 = polyLine3OnPlane(frontOutline2OnFrontPlane, frontPlane);
  }

  let backOutline3: PolyLine3 | undefined;
  let backOutline2OnBackPlane: PolyLine2 | undefined;
  if (!!wallCut.opening?.back?.opening3) {
    backOutline3 = wallOpeningOpening3(wallCut.opening, "BACK");
    assertIsNotNullOrUndefined(backOutline3);
    backOutline2OnBackPlane = planeProjectPolyLine3(backPlane, backOutline3);
  } else {
    backOutline3 = polyLine3OnPlane(backHole.opening2, backPlacement.edgePlane);
    backOutline2OnBackPlane = planeProjectPolyLine3(backPlane, backOutline3);
    backOutline3 = polyLine3OnPlane(backOutline2OnBackPlane, backPlane);
  }

  const frontHoleOnFrontPlane: OpeningSide = {
    ...frontHole,
    opening3: frontOutline3,
    opening2: frontOutline2OnFrontPlane,
  };

  const backHoleOnBackPlane: OpeningSide = {
    ...backHole,
    opening3: backOutline3,
    opening2: backOutline2OnBackPlane,
  };

  const [px, py] = planeNormal(frontPlane);

  return wallOpeningNew({
    handle: undefined,
    hasJamb: false, // no wall cuts have jambs
    hasWrapping: false, // no wall cuts have wrapping
    hasExteriorCasing: false,
    ...wallCut.opening,
    casingClipsFinish: !!wallCut.opening?.casingClipsFinish,
    hasSill: !!wallCut.opening?.hasSill,
    mirror: !!wallCut.opening?.mirror,
    front: frontHoleOnFrontPlane,
    back: backHoleOnBackPlane,
    frontPlane,
    backPlane,
    frontRoom: frontPlacement.room,
    backRoom: backPlacement.room,
    planExtents: planExtentsFromElevations([px, py], frontOutline3, backOutline3),
    planExtentsCasing: planExtentsFromElevations([px, py], frontOutline3, backOutline3),
  });
}
